import * as React from "react";
import { useLingui } from "@lingui/react";
import { msg } from "@lingui/core/macro";
import { AlertCircle, Lock, RotateCw, SearchX, type LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { errorStatus, errorToastText } from "@/lib/translateBackendError";

const fallbackMessage = msg({
  id: "common.queryError.fallback",
  message: "Check your connection and try again.",
});

export interface QueryErrorStateProps {
  error: unknown;
  /** Shows a retry button. Omitted for 403 and 404, where retrying cannot help. */
  onRetry?: () => void;
  /** One line for a card or a table body instead of the centred block. */
  compact?: boolean;
  /** Overrides the default "Could not load this". */
  title?: React.ReactNode;
  /** A second way out next to retry, such as clearing the filters that caused the error. */
  action?: React.ReactNode;
  className?: string;
}

/**
 * The failed-load state of a query: what went wrong in words the user can act
 * on, and a way to try again. A 403 and a 404 get their own wording because
 * the generic "try again" is wrong advice for both.
 */
export function QueryErrorState({
  error,
  onRetry,
  compact = false,
  title,
  action,
  className,
}: QueryErrorStateProps) {
  const { i18n } = useLingui();
  const status = errorStatus(error);

  let Icon: LucideIcon = AlertCircle;
  let message: string;
  let canRetry = !!onRetry;
  if (status === 403) {
    Icon = Lock;
    canRetry = false;
    message = i18n._({
      id: "common.queryError.forbidden",
      message: "You do not have access to this. Ask your administrator.",
    });
  } else if (status === 404) {
    Icon = SearchX;
    canRetry = false;
    message = i18n._({
      id: "common.queryError.notFound",
      message: "This record no longer exists.",
    });
  } else {
    message = errorToastText(error, i18n, fallbackMessage);
  }

  const heading =
    title ?? i18n._({ id: "common.queryError.title", message: "Could not load this" });
  const retryLabel = i18n._({ id: "common.retry", message: 'Try again' });

  // Compact states sit in cards, several to a page; `status` keeps them from
  // interrupting each other the way stacked alerts would.
  if (compact) {
    return (
      <div
        role="status"
        className={cn("flex min-w-0 items-center gap-2 py-2 text-sm", className)}
      >
        <Icon className="h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
        <span className="shrink-0 font-medium">{heading}</span>
        <span className="min-w-0 truncate text-muted-foreground" title={message}>
          {message}
        </span>
        {canRetry && (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="ml-auto h-auto shrink-0 px-0"
            onClick={onRetry}
          >
            {retryLabel}
          </Button>
        )}
        {action}
      </div>
    );
  }

  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col items-center gap-3 px-4 py-10 text-center",
        className
      )}
    >
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
        <Icon className="h-5 w-5 text-destructive" aria-hidden="true" />
      </span>
      <div className="space-y-1">
        <p className="font-medium">{heading}</p>
        <p className="max-w-prose text-sm text-muted-foreground">{message}</p>
      </div>
      {(canRetry || action) && (
        <div className="flex flex-wrap justify-center gap-2">
          {canRetry && (
            <Button type="button" variant="outline" size="sm" onClick={onRetry}>
              <RotateCw aria-hidden="true" />
              {retryLabel}
            </Button>
          )}
          {action}
        </div>
      )}
    </div>
  );
}
